import { canUseDOM } from '@ps/web-core-utils';
import type { CasinoGameGrid } from '@ps/web-martech-cms-types';
import { applyPatch } from 'fast-json-patch';
import produce from 'immer';
import { filter, takeUntil } from 'rxjs/operators';

import {
  clearState,
  extractPromoStream,
  favorites,
  freeSpinBonus,
  getIcon,
  injectPromo,
  isFSB,
  preload,
  set,
  setLoading,
  transformParams,
  userLoggedIn,
  userLoggedOut,
  userUpdates
} from '@swf/casino';
import type { UseWidget } from '@swf/core/hooks/useWidget';
import { useWidget } from '@swf/core/hooks/useWidget';
import { areGamesVisibleByConfig } from '@swf/core/models/casino/utils';
import type {
  EffectParams,
  ExtractDispatchFromModel,
  ExtractSelectFromModel,
  Slicer,
  WidgetEffectParams,
  WidgetModelConfig
} from '@swf/core/store';
import { select } from '@swf/core/store';
import type { CasinoGame, StreamData } from '@swf/core/types/CasinoGame';
import { currencyChange, logout } from '@swf/core/utils/actions';
import { isVisibleByAuth } from '@swf/core/utils/casino';

export interface CasinoGameGridWidgetState extends StreamData<CasinoGame> {
  loading: boolean;
  currentPage: number;
  complete: boolean;
  visibleByAuth: boolean;
}

const reducers = {
  favorites,
  freeSpinBonus,
  set,
  setLoading
};

const nextPage = (actions: EffectParams['actions'], id: string) =>
  actions.pipe(filter(({ type }) => type === `${id}/loadMore`));

const effects = {
  clearState,
  preload,
  userLoggedIn,
  userLoggedOut,
  userUpdates,
  async fetch(payload: undefined, params: WidgetEffectParams<CasinoGameGrid>) {
    const {
      actions,
      dispatch,
      select,
      requestStream,
      request,
      rootState,
      widget: {
        uid,
        game_service: service,
        params: body,
        logged_in_only: loggedInOnly,
        hide_on_login: hideOnLogin
      }
    } = params;
    const widgetDispatch: ExtractDispatchFromModel<CasinoGameGridWidgetModel> = dispatch[uid];
    const widgetSelect: ExtractSelectFromModel<CasinoGameGridWidgetModel> = select[uid];
    const options = { service, body: transformParams(body) };
    const isAuthenticated = select.session.isClientAuthenticated(rootState);
    const { locale, promotionStream } = extractPromoStream(params.widget, rootState);
    const onLogout = logout(actions) as NonNullable<ReturnType<typeof logout>>;

    widgetDispatch.set({ visibleByAuth: true });

    if (
      !areGamesVisibleByConfig({
        hasState: widgetSelect.get(rootState)?.games?.length > 0,
        params,
        service
      }) ||
      !isVisibleByAuth(loggedInOnly, hideOnLogin, isAuthenticated)
    ) {
      widgetDispatch.set({ visibleByAuth: false });
      return;
    }

    const renderIcon = widgetSelect.getIcon(rootState, params.widget);
    if (!canUseDOM) {
      const { body: data } = await request<StreamData<CasinoGame>>(options);
      widgetDispatch.set({
        ...injectPromo(data, 0, locale, () => renderIcon, promotionStream),
        currentPage: 0,
        loading: false
      });
    } else {
      const data = widgetSelect.get(rootState);
      const loading = !(data.games.length > 0);
      widgetDispatch.set({ loading, complete: data.games.length < body.psz });
      requestStream<StreamData<CasinoGame>>(options, undefined)
        .pipe(
          takeUntil(currencyChange(actions) as typeof actions),
          takeUntil(nextPage(actions, uid)),
          takeUntil(onLogout)
        )
        .subscribe((update) => {
          widgetDispatch.set({
            ...injectPromo(update, 0, locale, () => renderIcon, promotionStream),
            loading: false,
            currentPage: 0,
            complete: update?.games?.length < body.psz
          });
        });
    }
  },

  async loadMore(
    payload: undefined,
    {
      actions,
      rootState,
      request,
      requestStream,
      dispatch,
      widget: { uid, game_service: service, params }
    }: WidgetEffectParams<CasinoGameGrid>
  ) {
    const widgetDispatch: ExtractDispatchFromModel<CasinoGameGridWidgetModel> = dispatch[uid];
    const widgetSelect: ExtractSelectFromModel<CasinoGameGridWidgetModel> = select[uid];
    const data = widgetSelect.get(rootState);
    const options = {
      service,
      body: { ...transformParams(params), patch: true, pg: data.currentPage + 1 }
    };
    const onLogout = logout(actions) as NonNullable<ReturnType<typeof logout>>;

    widgetDispatch.setLoading(true);
    const { body: patches } = await request(options);
    const patchedData = produce(data, (draft) => {
      applyPatch(draft, patches);
      draft.currentPage = options.body.pg;
      draft.loading = false;
      draft.complete = !patches.length;
    });

    widgetDispatch.set(patchedData);

    requestStream<StreamData<CasinoGame>>(options, patchedData)
      .pipe(
        takeUntil(currencyChange(actions) as typeof actions),
        takeUntil(nextPage(actions, uid)),
        takeUntil(onLogout)
      )
      .subscribe((update) => widgetDispatch.set(update));
  }
};

const selectors = (slice: Slicer<CasinoGameGridWidgetState>) => ({
  get() {
    return slice;
  },
  getIcon,
  isFSB
});

export interface CasinoGameGridWidgetModel
  extends WidgetModelConfig<CasinoGameGrid, CasinoGameGridWidgetState> {
  reducers: typeof reducers;
  effects: typeof effects;
  selectors: typeof selectors;
}

export const gridWidgetModel: CasinoGameGridWidgetModel = {
  state: {
    loading: false,
    currentPage: 0,
    games: [],
    subscriptions: {},
    complete: false,
    visibleByAuth: true
  },
  reducers,
  effects,
  selectors
};

export const useGridWidgetModel: UseWidget<CasinoGameGrid, CasinoGameGridWidgetModel> = useWidget;
